import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal as AntModal, Button as AntButton, Table, Input as AntInput, Radio, message } from 'antd';

const MAX_ROWS = 50;
export const PASTE_MODE_ADD = 'add';
export const PASTE_MODE_OVERWRITE = 'overwrite';

export const TRAVELER_PASTE_COLUMNS = [
  { key: '연령구분', label: '연령구분', aliases: ['연령구분', '예약연령', '연령', '나이', 'age'] },
  { key: '한글이름', label: '한글이름', aliases: ['한글이름', '이름', '성명', 'koreanname', 'name'] },
  { key: '성별', label: '성별', aliases: ['성별', 'gender', 'sex'] },
  { key: '영문성', label: '영문성', aliases: ['영문성', '성', 'lastname', 'surname', 'familyname'] },
  { key: '영문이름', label: '영문이름', aliases: ['영문이름', '영문', 'firstname', 'givenname'] },
  { key: '생년월일', label: '생년월일', aliases: ['생년월일', '생일', 'birth', 'birthday', 'dob', 'dateofbirth'] },
  { key: '여권번호', label: '여권번호', aliases: ['여권번호', 'passport', 'passportno', 'passportnumber'] },
  { key: '여권만료일', label: '여권만료일', aliases: ['여권만료일', '만료일', 'expiry', 'expire', 'passportexpiry'] },
  { key: '휴대폰', label: '휴대폰', aliases: ['휴대폰', '핸드폰', '전화', '연락처', 'phone', 'mobile', 'tel'] },
  { key: '이메일', label: '이메일', aliases: ['이메일', 'email', '메일', 'mail'] },
  { key: '룸번호', label: '룸번호', aliases: ['룸번호', '객실', 'room', 'roomno'] },
  { key: '여행객비고', label: '여행객비고', aliases: ['여행객비고', '비고', '메모', 'remark', 'note'] },
];

function emptyRow() {
  const row = { _id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
  TRAVELER_PASTE_COLUMNS.forEach((c) => { row[c.key] = ''; });
  return row;
}

function travelersToPasteRows(travelers) {
  const sorted = [...(Array.isArray(travelers) ? travelers : [])].sort((a, b) => {
    const aa = Number.isFinite(Number(a?.순번)) ? Number(a.순번) : 999999;
    const bb = Number.isFinite(Number(b?.순번)) ? Number(b.순번) : 999999;
    return aa - bb;
  }).slice(0, MAX_ROWS);

  return sorted.map((t, idx) => {
    const row = emptyRow();
    row._id = `existing-${String(t?.여행객번호 || t?.여행객키 || idx)}-${idx}`;
    TRAVELER_PASTE_COLUMNS.forEach((c) => {
      let v = String(t?.[c.key] ?? '').trim();
      if (c.key === '연령구분' && v) v = normalizeAgeValueForPaste(v);
      if (c.key === '성별') v = normalizeGenderValue(v);
      if (c.key === '생년월일' || c.key === '여권만료일') v = normalizeDateValue(v);
      row[c.key] = v;
    });
    return row;
  });
}

function mergePasteOntoExisting(existingRows, pastedRows) {
  const base = (Array.isArray(existingRows) && existingRows.length)
    ? existingRows.map((r) => ({ ...r }))
    : [];
  const pasted = Array.isArray(pastedRows) ? pastedRows : [];
  const limit = Math.min(base.length, pasted.length);

  for (let i = 0; i < limit; i += 1) {
    const src = pasted[i] || {};
    TRAVELER_PASTE_COLUMNS.forEach((c) => {
      const v = String(src[c.key] ?? '').trim();
      if (!v) return; // 빈 칸은 기존 값 유지
      base[i][c.key] = v;
    });
  }
  return { rows: base.length ? base : [emptyRow()], overflow: Math.max(0, pasted.length - base.length) };
}

function normalizeHeader(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_\-]/g, '')
    .replace(/\(.*?\)/g, '');
}

function findColumnKey(header) {
  const norm = normalizeHeader(header);
  if (!norm) return null;
  for (const col of TRAVELER_PASTE_COLUMNS) {
    if (col.aliases.some((a) => normalizeHeader(a) === norm)) return col.key;
  }
  return null;
}

function excelSerialToDate(serial) {
  const n = Number(serial);
  if (!Number.isFinite(n) || n < 20000 || n > 80000) return null;
  // Excel serial date (1900 date system); adjust for Excel leap-year bug
  const utc = Date.UTC(1899, 11, 30) + Math.round(n) * 86400000;
  const d = new Date(utc);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeDateValue(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const serial = excelSerialToDate(s);
  if (serial) return serial;
  const cleaned = s.replace(/[./]/g, '-').replace(/\s+/g, '');
  const m1 = cleaned.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m1) {
    return `${m1[1]}-${m1[2].padStart(2, '0')}-${m1[3].padStart(2, '0')}`;
  }
  const m2 = cleaned.match(/^(\d{2})-(\d{1,2})-(\d{1,2})$/);
  if (m2) {
    const yy = Number(m2[1]);
    const full = yy >= 50 ? `19${m2[1]}` : `20${m2[1]}`;
    return `${full}-${m2[2].padStart(2, '0')}-${m2[3].padStart(2, '0')}`;
  }
  const m3 = cleaned.match(/^(\d{8})$/);
  if (m3) {
    return `${m3[1].slice(0, 4)}-${m3[1].slice(4, 6)}-${m3[1].slice(6, 8)}`;
  }
  return s;
}

export function normalizeGenderValue(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  if (['남', '남성', '남자', 'm', 'male', 'man'].includes(lower) || s === '男') return '남성';
  if (['여', '여성', '여자', 'f', 'female', 'woman'].includes(lower) || s === '女') return '여성';
  return s;
}

export function normalizeAgeValueForPaste(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '성인';
  if (s.includes('성인') || /adult/i.test(s)) return '성인';
  const m = s.match(/(\d{1,2})\s*세?/);
  if (m) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && n >= 0 && n <= 18) return String(n);
  }
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    if (n >= 0 && n <= 18) return String(n);
  }
  return '성인';
}

function parseTsvText(text) {
  const lines = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((line) => line.trim().length > 0);
  return lines.map((line) => line.split('\t').map((c) => String(c ?? '').trim()));
}

function parseHtmlTable(html) {
  try {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    const table = doc.querySelector('table');
    if (!table) return [];
    return Array.from(table.querySelectorAll('tr')).map((tr) =>
      Array.from(tr.querySelectorAll('th,td')).map((cell) => String(cell.textContent || '').trim())
    ).filter((row) => row.some((c) => c));
  } catch {
    return [];
  }
}

function mapMatrixToRows(matrix) {
  if (!Array.isArray(matrix) || matrix.length === 0) return [];

  const first = matrix[0] || [];
  const mappedKeys = first.map((h) => findColumnKey(h));
  const headerHitCount = mappedKeys.filter(Boolean).length;
  const hasHeader = headerHitCount >= 2;

  let dataRows = matrix;
  let keyOrder = TRAVELER_PASTE_COLUMNS.map((c) => c.key);

  if (hasHeader) {
    dataRows = matrix.slice(1);
    keyOrder = mappedKeys;
  }

  const rows = [];
  for (const cells of dataRows) {
    if (!Array.isArray(cells) || cells.every((c) => !String(c ?? '').trim())) continue;
    const row = emptyRow();
    if (hasHeader) {
      keyOrder.forEach((key, idx) => {
        if (!key) return;
        row[key] = String(cells[idx] ?? '').trim();
      });
    } else {
      keyOrder.forEach((key, idx) => {
        row[key] = String(cells[idx] ?? '').trim();
      });
    }

    row.연령구분 = String(row.연령구분 || '').trim()
      ? normalizeAgeValueForPaste(row.연령구분)
      : '';
    row.성별 = normalizeGenderValue(row.성별);
    row.생년월일 = normalizeDateValue(row.생년월일);
    row.여권만료일 = normalizeDateValue(row.여권만료일);

    const hasAny = TRAVELER_PASTE_COLUMNS.some((c) => String(row[c.key] || '').trim());
    if (hasAny) rows.push(row);
    if (rows.length >= MAX_ROWS) break;
  }
  return rows;
}

function parseClipboardData(clipboardData) {
  const html = clipboardData?.getData?.('text/html') || '';
  const text = clipboardData?.getData?.('text/plain') || '';
  const fromHtml = html ? parseHtmlTable(html) : [];
  if (fromHtml.length > 0) return mapMatrixToRows(fromHtml);
  if (text) return mapMatrixToRows(parseTsvText(text));
  return [];
}

/**
 * 명단 엑셀/클립보드 붙여넣기 모달
 * - 예약정보 컬럼 테이블에 붙여넣으면 인원 수만큼 행이 채워짐
 * - onConfirm(rows, { mode }) 호출
 *   - mode: 'add' | 'overwrite'
 */
export default function BulkTravelerPasteModal({
  open,
  onClose,
  onConfirm,
  confirming = false,
  existingTravelers = [],
}) {
  const [rows, setRows] = useState(() => [emptyRow()]);
  const [mode, setMode] = useState(PASTE_MODE_ADD);
  const [seededFromExisting, setSeededFromExisting] = useState(false);
  const pasteZoneRef = useRef(null);

  const existingCount = useMemo(
    () => (Array.isArray(existingTravelers) ? existingTravelers.length : 0),
    [existingTravelers]
  );

  const seedExistingRows = () => {
    const seeded = travelersToPasteRows(existingTravelers);
    setRows(seeded.length ? seeded : [emptyRow()]);
    setSeededFromExisting(true);
  };

  useEffect(() => {
    if (open) {
      setRows([emptyRow()]);
      setMode(PASTE_MODE_ADD);
      setSeededFromExisting(false);
      setTimeout(() => {
        try { pasteZoneRef.current?.focus?.(); } catch { /* ignore */ }
      }, 100);
    }
  }, [open]);

  const handleModeChange = (nextMode) => {
    if (confirming || nextMode === mode) return;
    setMode(nextMode);
    if (nextMode === PASTE_MODE_OVERWRITE) {
      seedExistingRows();
      if (existingCount === 0) {
        message.warning('덮어쓸 기존 명단이 없습니다.');
      } else {
        message.info(`기존 명단 ${existingCount}명을 불러왔습니다. 수정하거나 엑셀을 붙여넣으세요.`);
      }
      return;
    }
    setRows([emptyRow()]);
    setSeededFromExisting(false);
  };

  const handleClose = () => {
    if (confirming) return;
    setRows([emptyRow()]);
    setMode(PASTE_MODE_ADD);
    setSeededFromExisting(false);
    onClose?.();
  };

  const applyParsedRows = (parsed) => {
    if (!parsed.length) {
      message.warning('붙여넣을 표 데이터를 찾지 못했습니다. 엑셀에서 표 영역을 복사한 뒤 다시 붙여넣으세요.');
      return;
    }

    if (mode === PASTE_MODE_OVERWRITE) {
      const base = seededFromExisting
        ? rows
        : travelersToPasteRows(existingTravelers);
      const { rows: merged, overflow } = mergePasteOntoExisting(base, parsed);
      setRows(merged);
      setSeededFromExisting(true);
      if (overflow > 0) {
        message.success(`${Math.min(parsed.length, merged.length)}명의 명단이 기존 행에 반영되었습니다. (초과 ${overflow}명은 무시)`);
      } else {
        message.success(`${Math.min(parsed.length, merged.length)}명의 명단이 기존 행에 반영되었습니다.`);
      }
      return;
    }

    setRows(parsed);
    message.success(`${parsed.length}명의 명단이 반영되었습니다.`);
  };

  const handlePaste = (e) => {
    const cd = e?.clipboardData;
    if (!cd) return;
    const parsed = parseClipboardData(cd);
    if (parsed.length === 0) return; // allow default paste into inputs
    e.preventDefault();
    e.stopPropagation();
    applyParsedRows(parsed);
  };

  const handlePasteFromClipboard = async () => {
    try {
      if (!navigator?.clipboard?.readText) {
        message.info('이 브라우저에서는 클립보드 읽기가 제한됩니다. 표 영역에서 Ctrl+V로 붙여넣으세요.');
        pasteZoneRef.current?.focus?.();
        return;
      }
      const text = await navigator.clipboard.readText();
      const parsed = mapMatrixToRows(parseTsvText(text));
      applyParsedRows(parsed);
    } catch {
      message.info('클립보드를 읽을 수 없습니다. 표 영역에서 Ctrl+V로 붙여넣으세요.');
      pasteZoneRef.current?.focus?.();
    }
  };

  const updateCell = (rowId, key, value) => {
    setRows((prev) => prev.map((r) => (r._id === rowId ? { ...r, [key]: value } : r)));
  };

  const removeRow = (rowId) => {
    if (mode === PASTE_MODE_OVERWRITE) {
      message.info('덮어쓰기 모드에서는 행을 삭제할 수 없습니다. 신규 추가 모드를 사용하세요.');
      return;
    }
    setRows((prev) => {
      const next = prev.filter((r) => r._id !== rowId);
      return next.length ? next : [emptyRow()];
    });
  };

  const addEmptyRow = () => {
    if (mode === PASTE_MODE_OVERWRITE) {
      message.info('덮어쓰기 모드에서는 기존 명단 행만 사용합니다.');
      return;
    }
    setRows((prev) => (prev.length >= MAX_ROWS ? prev : [...prev, emptyRow()]));
  };

  const filledCount = useMemo(
    () => rows.filter((r) => TRAVELER_PASTE_COLUMNS.some((c) => String(r[c.key] || '').trim())).length,
    [rows]
  );

  const handleOk = () => {
    // 덮어쓰기: 기존 행 순서 그대로 전송 (빈 행도 순번 매칭 유지)
    const sourceRows = mode === PASTE_MODE_OVERWRITE
      ? rows.slice(0, Math.min(MAX_ROWS, existingCount || rows.length))
      : rows.filter((r) => TRAVELER_PASTE_COLUMNS.some((c) => String(r[c.key] || '').trim()));

    const payload = sourceRows
      .slice(0, MAX_ROWS)
      .map((r) => {
        const out = {};
        TRAVELER_PASTE_COLUMNS.forEach((c) => {
          let v = String(r[c.key] ?? '').trim();
          if (c.key === '연령구분') {
            if (v) v = normalizeAgeValueForPaste(v);
            else if (mode !== PASTE_MODE_OVERWRITE) v = '성인';
          }
          if (c.key === '성별') v = normalizeGenderValue(v);
          if (c.key === '생년월일' || c.key === '여권만료일') v = normalizeDateValue(v);
          out[c.key] = v;
        });
        return out;
      });

    if (payload.length === 0) {
      message.warning(mode === PASTE_MODE_OVERWRITE
        ? '덮어쓸 명단이 없습니다.'
        : '등록할 명단이 없습니다. 엑셀 표를 붙여넣으세요.');
      return;
    }
    if (mode === PASTE_MODE_OVERWRITE && existingCount <= 0) {
      message.warning('덮어쓸 기존 명단이 없습니다. 먼저 명단을 추가하세요.');
      return;
    }
    onConfirm?.(payload, { mode });
  };

  const isOverwrite = mode === PASTE_MODE_OVERWRITE;
  const applyCount = isOverwrite
    ? Math.min(rows.length, Math.max(0, existingCount))
    : filledCount;
  const confirmLabel = isOverwrite
    ? (applyCount > 0 ? `${applyCount}명 덮어쓰기` : '덮어쓰기')
    : (filledCount > 0 ? `${filledCount}명 추가` : '추가');

  const columns = [
    {
      title: 'No',
      width: 48,
      fixed: 'left',
      render: (_v, _r, idx) => idx + 1,
    },
    ...TRAVELER_PASTE_COLUMNS.map((col) => ({
      title: col.label,
      dataIndex: col.key,
      key: col.key,
      width: col.key === '여행객비고' || col.key === '이메일' ? 140 : 100,
      render: (value, record) => (
        <AntInput
          size="small"
          value={value}
          onChange={(e) => updateCell(record._id, col.key, e.target.value)}
          placeholder={col.label}
        />
      ),
    })),
    ...(!isOverwrite ? [{
      title: '',
      key: 'actions',
      width: 56,
      fixed: 'right',
      render: (_v, record) => (
        <AntButton type="link" size="small" danger onClick={() => removeRow(record._id)}>
          삭제
        </AntButton>
      ),
    }] : []),
  ];

  return (
    <AntModal
      open={open}
      title="명단 엑셀 붙여넣기"
      width={1100}
      centered
      maskClosable={false}
      destroyOnClose
      onCancel={handleClose}
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-xs text-gray-500">
            {isOverwrite ? (
              <>
                기존 명단 <span className="font-semibold text-amber-600">{applyCount}</span>명
                {existingCount > 0 ? ` 표시 중` : ''}
              </>
            ) : (
              <>
                반영 인원 <span className="font-semibold text-blue-600">{filledCount}</span>명
                {filledCount > MAX_ROWS ? ` (최대 ${MAX_ROWS}명)` : ''}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <AntButton onClick={handleClose} disabled={confirming}>취소</AntButton>
            <AntButton onClick={handlePasteFromClipboard} disabled={confirming}>클립보드에서 붙여넣기</AntButton>
            {!isOverwrite && (
              <AntButton onClick={addEmptyRow} disabled={confirming || rows.length >= MAX_ROWS}>행 추가</AntButton>
            )}
            <AntButton
              type="primary"
              danger={isOverwrite}
              loading={confirming}
              onClick={handleOk}
            >
              {confirmLabel}
            </AntButton>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-gray-600 shrink-0">적용 방식</span>
          <Radio.Group
            size="small"
            value={mode}
            disabled={confirming}
            onChange={(e) => handleModeChange(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: '신규 추가', value: PASTE_MODE_ADD },
              { label: '기존 명단 덮어쓰기', value: PASTE_MODE_OVERWRITE },
            ]}
          />
          {isOverwrite && (
            <span className="text-xs text-gray-500">
              기존 명단이 아래에 표시됩니다. 엑셀 붙여넣기 시 값이 있는 칸만 덮어씁니다.
            </span>
          )}
        </div>

        <div className={`rounded-md border px-3 py-2 text-xs leading-relaxed ${
          isOverwrite
            ? 'border-amber-100 bg-amber-50 text-gray-700'
            : 'border-blue-100 bg-blue-50 text-gray-700'
        }`}
        >
          {isOverwrite
            ? (
              <>
                아래는 현재 예약의 기존 명단입니다. 셀을 직접 수정하거나, 엑셀에서 표를 복사한 뒤 <strong>Ctrl+V</strong>로 붙여넣으면 순번 순서대로 값이 있는 칸만 덮어씁니다.
              </>
            )
            : (
              <>
                엑셀에서 명단 표(헤더 포함 권장)를 복사한 뒤, 아래 표 영역을 클릭하고 <strong>Ctrl+V</strong>로 붙여넣으세요.
                인원 수만큼 슬롯이 추가되고 예약정보가 채워집니다.
              </>
            )}
          <div className="mt-1 text-gray-500">
            컬럼 예: 연령구분, 한글이름, 성별, 영문성, 영문이름, 생년월일, 여권번호, 여권만료일, 휴대폰, 이메일, 룸번호, 여행객비고
          </div>
        </div>

        <div
          ref={pasteZoneRef}
          tabIndex={0}
          onPaste={handlePaste}
          className="outline-none rounded-md border border-dashed border-gray-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 p-1"
        >
          <Table
            size="small"
            pagination={false}
            scroll={{ x: 1200, y: 360 }}
            rowKey="_id"
            columns={columns}
            dataSource={rows}
            locale={{
              emptyText: isOverwrite
                ? '표시할 기존 명단이 없습니다'
                : '여기 클릭 후 Ctrl+V로 엑셀 표를 붙여넣으세요',
            }}
          />
        </div>
      </div>
    </AntModal>
  );
}
